import { useState, useEffect } from "react";
import { callWithAuth, getUserSettings, updateUserSettings } from "@/api/Api";
import { getFeedTags } from "../api/feedApi";
import type { FeedTag } from "@shared_types";

/**
 * Persist the picked tags to the backend so the feed can personalize.
 * The PutUserSettings handler OVERWRITES the whole settings row, so we must
 * fetch the current settings and merge — sending only the tag fields would
 * wipe the user's other settings. Mirrors how iOS submits feed tags.
 * Fire-and-forget: a failure here must never block the user from the feed.
 */
async function persistFeedTags(selectedTags: string[]): Promise<void> {
  try {
    const current = await callWithAuth(getUserSettings);
    if (!current?.settings) return;
    await callWithAuth(updateUserSettings, {
      settings: {
        ...current.settings,
        userFeedTags: selectedTags,
        hasCompletedFeedTagsOnboarding: true,
      },
    });
  } catch (err) {
    console.error("[FeedTagsOnboarding] Failed to save feed tags:", err);
  }
}

const TAGS_SEEN_KEY = "feed_tags_seen";
const TAGS_SELECTED_KEY = "feed_selected_tags";

interface FeedTagsOnboardingProps {
  onDismiss: () => void;
  /** Called after the picks are saved to the backend, so the feed can refetch personalized. */
  onTagsSaved?: () => void;
}

function FeedTagsOnboarding({ onDismiss, onTagsSaved }: FeedTagsOnboardingProps) {
  const [tags, setTags] = useState<FeedTag[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    callWithAuth(getFeedTags)
      .then((res) => {
        setTags(res.tags ?? []);
        // iOS shows chips UNSELECTED by default (the user opts in to topics).
        setSelected(new Set());
      })
      .catch(() => {
        // Fetch failed — dismiss WITHOUT marking seen, so onboarding
        // retries on the next visit instead of being permanently skipped.
        onDismiss();
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTag = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const [submitting, setSubmitting] = useState(false);

  // Skip — discard any selection and persist an empty tag list (matches iOS
  // handleSkip, which saves userFeedTags: []). NOT a save of the current picks.
  const handleSkip = () => {
    localStorage.setItem(TAGS_SEEN_KEY, "true");
    localStorage.removeItem(TAGS_SELECTED_KEY);
    void persistFeedTags([]).then(() => onTagsSaved?.());
    onDismiss();
  };

  // Done — persist the selected picks (matches iOS handleDone). Only reachable
  // when at least one tag is selected.
  const handleDone = () => {
    const selectedTags = [...selected];
    localStorage.setItem(TAGS_SEEN_KEY, "true");
    if (selectedTags.length > 0) {
      localStorage.setItem(TAGS_SELECTED_KEY, JSON.stringify(selectedTags));
    }
    setSubmitting(true);
    // Send the picks to the backend in the background; don't block the UI.
    // Once saved, tell the parent to refetch so the feed re-sorts by the picks.
    void persistFeedTags(selectedTags).then(() => onTagsSaved?.());
    onDismiss();
  };

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-black px-6 pt-20 pb-24">
      {/* Header — left-aligned at the top (matches iOS) */}
      <div className="w-full max-w-md mx-auto flex flex-col gap-2">
        <h2 className="text-white text-3xl font-bold leading-tight">
          What topics do you care about right now?
        </h2>
        <p className="text-white/50 text-base">Personalize your video feed.</p>
      </div>

      {/* Tags — loose centered cluster */}
      <div className="flex-1 flex items-center justify-center">
        {loading ? (
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
        ) : (
          <div className="flex flex-wrap gap-2.5 justify-center max-w-md">
            {tags.map((tag) => {
              const isSelected = selected.has(tag.name);
              return (
                <button
                  key={tag.name}
                  type="button"
                  onClick={() => toggleTag(tag.name)}
                  // iOS TagChip (dark theme): bg #1a1a1a + white text always;
                  // selection only swaps the 2px border from #333 to brand blue.
                  className={`px-4 py-3 rounded-[20px] text-base font-medium border-2 bg-[#1a1a1a] text-white transition-all duration-150 active:scale-95 ${
                    isSelected
                      ? "border-[var(--color-blue)]"
                      : "border-[#333]"
                  }`}
                >
                  {tag.displayName}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer — Skip on the left, Done on the right (matches iOS; no pills).
          Done only appears once at least one topic is selected. */}
      <div className="w-full max-w-md mx-auto flex items-center justify-between">
        <button
          type="button"
          onClick={handleSkip}
          disabled={loading || submitting}
          className="text-white text-lg font-bold active:opacity-70 transition-opacity disabled:opacity-40"
        >
          Skip
        </button>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={handleDone}
            disabled={loading || submitting}
            className="text-white text-lg font-bold active:opacity-70 transition-opacity disabled:opacity-40"
          >
            {submitting ? "Saving..." : "Done"}
          </button>
        )}
      </div>
    </div>
  );
}

export { FeedTagsOnboarding };
export { TAGS_SEEN_KEY };
